// ============================================
// BOOKING DETAILS MODAL WITH DYNAMIC QR CODE
// Generates QR code from bookingNumber for verification
// ============================================

import { useState, useEffect } from 'react';

const BookingDetailsModal = ({ booking, onClose }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const bookingStatus = (
    booking?.status ||
    booking?.uiStatus ||
    'pending'
  ).toLowerCase();
  
  const isConfirmed =
  bookingStatus === 'confirmed' || bookingStatus === 'completed';

const isCancelled = bookingStatus === 'cancelled';

const showQr = isConfirmed || isCancelled;

  // Generate QR code dynamically
  useEffect(() => {
    const generateQRCode = () => {
      console.log("BOOKING DETAILS", booking);
      // Use bookingNumber for QR code (most reliable identifier)
      if (bookingStatus === 'pending') return;
      
      const bookingCode = booking?.bookingReference;
      
      console.log('Generating QR for booking:', bookingCode);
      
      if (bookingCode) {
        setLoadingQr(true);
        try {
          // Encode booking number with reference for verification
          const qrData = `${bookingCode}`;
          const encodedCode = encodeURIComponent(qrData);
          
          // Generate QR code URL
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodedCode}&margin=10`;
          
          setQrCodeUrl(qrUrl);
          // console.log('QR Code generated:', qrUrl);
        } catch (error) {
          console.error('Error generating QR code:', error);
        } finally {
          setLoadingQr(false);
        }
      }
    };

    generateQRCode();
  }, [booking?.bookingNumber]);

  if (!booking) return null;

  const {
    bookingNumber,
    bookingReference,
    movieTitle,
    cinemaName,
    cinemaLocation,
    showDate,
    seatsLabel,
    seatCount,
    totalAmount,
    status,
    paymentStatus,
    createdAt,
    bookingDetails = {},
  } = booking;

  const {
    cinema = {},
    showtime = {},
    payment = {},
    pricing = {},
    customerName = '',
    customerEmail = '',
    customerPhone = '',
    movie = {},
    promotionalItems = [],
  } = bookingDetails;

  const convertTo12HourFormat = (time24) => {
    if (!time24) return 'N/A';
    
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    
    return `${hour12}:${minutes} ${ampm}`;
  };

  const couponDiscount = pricing?.couponDiscount || 0;
  const finalPaid = parseFloat(totalAmount || 0);


  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>Booking Details</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '30px',
              height: '30px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {/* Movie Details */}
          <section style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e9ecef' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Movie Information</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Movie Title:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{movieTitle}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Rating:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{movie?.rating || 'N/A'}</span>
            </div>
          </section>

          {/* Cinema Details */}
          <section style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e9ecef' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Cinema Information</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Cinema Name:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{cinemaName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Location:</span>
              <span style={{ color: '#333', textAlign: 'right', maxWidth: '50%' }}>
                {cinemaLocation || cinema?.location || 'N/A'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Screen:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{showtime?.screenName || 'N/A'}</span>
            </div>
          </section>

          {/* Show Timing */}
          <section style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e9ecef' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Show Timing</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Date:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{showDate}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Time:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>
                {convertTo12HourFormat(booking.showTime)}
              </span>
            </div>
          </section>

          {/* Seats */}
          <section style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e9ecef' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Seats</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Selected Seats:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{seatsLabel}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Number of Seats:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{seatCount}</span>
            </div>
          </section>

          {/* Price Breakdown */}
          {/* Price Breakdown */}
<section style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e9ecef' }}>
  <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>
    Price Breakdown
  </h3>

  {/* Ticket Price */}
  {pricing?.ticketPrice > 0 && (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
      <span style={{ color: '#666', fontWeight: '500' }}>Ticket Price:</span>
      <span style={{ color: '#333' }}>
        ₹{pricing.ticketPrice.toFixed(2)}
      </span>
    </div>
  )}

  {/* Snacks */}
  {pricing?.snacksAmount > 0 && (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
      <span style={{ color: '#666', fontWeight: '500' }}>Snacks:</span>
      <span style={{ color: '#333' }}>
        ₹{pricing.snacksAmount.toFixed(2)}
      </span>
    </div>
  )}

  {/* Promotional Items (e.g. Website Launch Offer — Free 35g Popcorn) */}
  {Array.isArray(promotionalItems) && promotionalItems.length > 0 && (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
      <span style={{ color: '#28a745', fontWeight: '500' }}>
        🎁 {promotionalItems[0].name} (Website Launch Offer):
      </span>
      <span style={{ color: '#28a745', fontWeight: '600' }}>FREE</span>
    </div>
  )}

  {/* Convenience Fee (Base + GST) */}
  {(pricing?.convenienceFeeBase || 0) + (pricing?.convenienceFeeGST || 0) > 0 && (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
        <span style={{ color: '#666', fontWeight: '500' }}>Convenience Fee:</span>
        <span style={{ color: '#333' }}>
          ₹{(
            (pricing.convenienceFeeBase || 0) +
            (pricing.convenienceFeeGST || 0)
          ).toFixed(2)}
        </span>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 0 6px 20px',
        fontSize: '0.9em',
        color: '#6c757d'
      }}>
        <span>Base Amount:</span>
        <span>₹{(pricing.convenienceFeeBase || 0).toFixed(2)}</span>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 0 6px 20px',
        fontSize: '0.9em',
        color: '#6c757d'
      }}>
        <span>Integrated GST (IGST) @ 18%:</span>
        <span>₹{(pricing.convenienceFeeGST || 0).toFixed(2)}</span>
      </div>
    </>
  )}

  {/* Coupon */}
  {pricing?.couponDiscount > 0 && (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
      <span style={{ color: '#666', fontWeight: '500' }}>Coupon Discount:</span>
      <span style={{ color: '#28a745', fontWeight: '600' }}>
        -₹{pricing.couponDiscount.toFixed(2)}
      </span>
    </div>
  )}

  <hr style={{ border: 'none', borderTop: '1px solid #e9ecef', margin: '10px 0' }} />

  {/* Final Total */}
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    fontWeight: 'bold',
    fontSize: '16px',
    color: '#2c3e50'
  }}>
    <span>Total Amount Paid:</span>
    <span>
      ₹{(pricing?.totalAmount || parseFloat(totalAmount || 0)).toFixed(2)}
    </span>
  </div>
</section>


          {/* QR Code Section - For Verification */}
          {qrCodeUrl && showQr && (
            <section style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e9ecef' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>QR Code (Booking Verification)</h3>
              {loadingQr ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Generating QR code...
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '15px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                }}>
                  <img
                    src={qrCodeUrl}
                    alt="Booking QR Code"
                    style={{
                      width: '250px',
                      height: '250px',
                      border: '3px solid #dee2e6',
                      borderRadius: '4px',
                      objectFit: 'contain',
                      backgroundColor: '#fff',
                      padding: '10px',
                    }}
                    onError={() => console.error('Failed to load QR code image')}
                  />
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '13px', fontWeight: '500' }}>
                      Scan at cinema for verification
                    </p>
                    <p style={{ margin: '4px 0', color: '#999', fontSize: '12px' }}>
                      Booking ID: <strong>{bookingReference}</strong>
                    </p>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Booking Information */}
          <section style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e9ecef' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Booking Information</h3>
            {/* <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Booking ID:</span>
              <span style={{ color: '#333', textAlign: 'right', fontFamily: 'monospace', fontSize: '13px' }}>{bookingNumber}</span>
            </div> */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Booking ID:</span>
              <span style={{ color: '#333', textAlign: 'right', fontFamily: 'monospace' }}>
              {showQr
  ? bookingReference
  : 'Will be available after confirmation'}
</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Status:</span>
              <span style={{ 
                color: status === 'cancelled' ? '#dc3545' : status === 'confirmed' ? '#28a745' : '#ffc107',
                fontWeight: '600'
              }}>
                {(status || 'pending').toUpperCase()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Payment:</span>
              <span style={{ color: '#333' }}>{(paymentStatus || 'pending').toUpperCase()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Method:</span>
              <span style={{ color: '#333' }}>
                {booking?.paymentMethod
                  ? String(booking.paymentMethod).toUpperCase()
                  : 'N/A'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Booked On:</span>
              <span style={{ color: '#333' }}>{new Date(createdAt).toLocaleDateString()}</span>
            </div>
          </section>

          {/* Customer Details */}
          <section>
            <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Customer Information</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Name:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{customerName || 'N/A'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Email:</span>
              <span style={{ color: '#333', textAlign: 'right', wordBreak: 'break-word' }}>{customerEmail || 'N/A'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: '#666', fontWeight: '500' }}>Phone:</span>
              <span style={{ color: '#333', textAlign: 'right' }}>{customerPhone || 'N/A'}</span>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export {
  BookingDetailsModal,
};